import React, { useMemo, useCallback } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Checkbox } from './ui/checkbox';
import { AssignmentRow } from './AssignmentRow';
import { DataTable } from '@/components/tables/data-table';
import { createAssignmentColumns } from '@/components/tables/assignment-columns';

// Flexible assignment interface that matches API response
export interface ApiAssignment {
  id?: string | number;
  campaignId?: string | number;
  campaignName?: string;
  taskId?: string | number;
  taskName?: string;
  packId?: string | number;
  packName?: string;
  storeId?: string | number;
  storeName?: string;
  operatorId?: string | number;
  operatorName?: string;
  formatId?: string | number;
  mediaFormatId?: string | number;
  mediaFormatName?: string;
  formatName?: string;
  quantity?: number;
  expectedQuantity?: number;
  delivered?: number;
  deliveredQuantity?: number;
  comments?: string;
  status?: string;
  isDeleted?: boolean;
  createdAt?: string;
  updatedAt?: string;
  targetQuantity?: number;
  // Phase 4: Task type and removal context
  taskType?: 'POSTING' | 'REMOVAL';
  removalType?: 'planned' | 'confirmed' | 'possible' | null;
  removalSourceType?: 'planned_delivery' | 'confirmed_delivery' | 'left_with_manager' | string | null;
  referenceCycleId?: number | null;
  assignmentSource?: 'AUTO_FROM_POSTING' | 'MANUAL_OVERRIDE';
  [key: string]: any; // Allow additional fields
}

export interface AssignmentTableProps {
  assignments: ApiAssignment[];
  operatorLookup?: Map<number, string>;
  // New props for assignment management
  selectedAssignments?: number[];
  onSelectionChange?: (selected: number[]) => void;
  onStatusChange?: (assignmentId: number, newStatus: string) => void;
  onViewHistory?: (assignmentId: number) => void;
  onDelete?: (assignmentId: number) => void;
  onReassign?: (assignment: ApiAssignment) => void;  // Phase 4: Reassignment handler
  getStatusBadge?: (assignment: ApiAssignment | string) => React.ReactNode; // Accept full assignment for derived status
  showActions?: boolean;
  showRemovalBasis?: boolean;
  onViewEvidence?: (assignmentId: number) => void;
  isLoading?: boolean;
}

type CampaignGroup = {
  key: string;
  campaignName: string;
  taskName: string;
  assignments: ApiAssignment[];
};

function getRemovalBasisLabel(assignment: ApiAssignment) {
  if (assignment.taskType === 'POSTING') return 'Posting';
  if (assignment.taskType !== 'REMOVAL') return null;
  if (assignment.removalType === 'possible' || assignment.removalSourceType === 'left_with_manager') {
    return 'Possible';
  }
  if (assignment.removalType === 'confirmed' || assignment.removalSourceType === 'confirmed_delivery') {
    return 'Confirmed';
  }
  return 'Planned';
}

function getRemovalBasisClassName(label: string) {
  if (label === 'Confirmed') {
    return 'border-green-200 bg-green-50 text-green-700';
  }
  if (label === 'Mixed') {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }
  if (label === 'Possible') {
    return 'border-dashed border-amber-400 bg-amber-50 text-amber-800';
  }
  if (label === 'Posting') {
    return 'border-gray-200 bg-gray-50 text-gray-600';
  }
  return 'border-blue-200 bg-blue-50 text-blue-700';
}

function getRemovalBasisGroupLabel(assignments: ApiAssignment[]) {
  const labels = Array.from(new Set(assignments.map(getRemovalBasisLabel).filter(Boolean)));
  if (labels.length === 0) return null;
  if (labels.length > 1) return 'Mixed';
  return labels[0] as string;
}

export function AssignmentTable({
  assignments,
  operatorLookup,
  selectedAssignments = [],
  onSelectionChange,
  onStatusChange,
  onViewHistory,
  onDelete,
  onReassign,
  getStatusBadge,
  showActions = false,
  showRemovalBasis = false,
  onViewEvidence,
  isLoading = false,
}: AssignmentTableProps) {
  if (!assignments || assignments.length === 0) {
    return <div className="text-sm text-gray-600">No assignments found.</div>;
  }

  // Memoize columns
  const columns = useMemo(
    () =>
      createAssignmentColumns({
        onStatusChange,
        onViewHistory,
        onDelete,
        onReassign,
        onViewEvidence,
        getStatusBadge,
      }),
    [onStatusChange, onViewHistory, onDelete, onReassign, onViewEvidence, getStatusBadge]
  );

  // Handle selection changes from DataTable
  const handleDataTableSelectionChange = useCallback(
    (selectedRows: ApiAssignment[]) => {
      if (onSelectionChange) {
        const selectedIds = selectedRows
          .map((row) => Number(row.id))
          .filter((id) => !isNaN(id));
        onSelectionChange(selectedIds);
      }
    },
    [onSelectionChange]
  );

  // If we have selection/action props, use TanStack Table
  if (showActions || onSelectionChange) {
    return (
      <DataTable
        columns={columns}
        data={assignments}
        searchKey="campaignName"
        searchPlaceholder="Search campaigns..."
        showColumnToggle={false}
        isLoading={isLoading}
        onSelectionChange={onSelectionChange ? handleDataTableSelectionChange : undefined}
      />
    );
  }

  // Group assignments by operator, then by store within each operator
  const groupedAssignments = useMemo(() => {
    const operatorGroups = new Map<string, {
      operatorId: string | number;
      operatorName: string;
      stores: Map<string, {
        storeId: string | number;
        storeName: string;
        campaignGroups: CampaignGroup[];
      }>;
    }>();

    assignments.forEach(assignment => {
      const operatorId = assignment.operatorId || 'unknown';
      const operatorName = assignment.operatorName || `Operator ${operatorId}`;
      const storeId = assignment.storeId || 'unknown';
      const storeName = assignment.storeName || `Store ${storeId}`;

      // Get or create operator group
      if (!operatorGroups.has(String(operatorId))) {
        operatorGroups.set(String(operatorId), {
          operatorId,
          operatorName,
          stores: new Map()
        });
      }

      const operatorGroup = operatorGroups.get(String(operatorId))!;

      // Get or create store group within operator
      if (!operatorGroup.stores.has(String(storeId))) {
        operatorGroup.stores.set(String(storeId), {
          storeId,
          storeName,
          campaignGroups: []
        });
      }

      const storeGroup = operatorGroup.stores.get(String(storeId))!;
      const campaignName = assignment.campaignName || 'Unknown Campaign';
      const taskName = assignment.taskName || assignment.packName || 'Unknown Task';
      const campaignKey = `${campaignName}__${taskName}`;
      const existingGroup = storeGroup.campaignGroups.find((group) => group.key === campaignKey);

      if (existingGroup) {
        existingGroup.assignments.push(assignment);
      } else {
        storeGroup.campaignGroups.push({
          key: campaignKey,
          campaignName,
          taskName,
          assignments: [assignment],
        });
      }
    });

    return Array.from(operatorGroups.values()).map((operatorGroup) => ({
      ...operatorGroup,
      stores: Array.from(operatorGroup.stores.values()).map((storeGroup) => ({
        ...storeGroup,
        campaignGroups: storeGroup.campaignGroups
          .map((campaignGroup) => ({
            ...campaignGroup,
            assignments: [...campaignGroup.assignments].sort((a, b) => {
              const formatCompare = String(a.mediaFormatName || a.formatName || '').localeCompare(
                String(b.mediaFormatName || b.formatName || '')
              );
              if (formatCompare !== 0) return formatCompare;

              return String(a.realAssignmentId ?? a.id ?? '').localeCompare(
                String(b.realAssignmentId ?? b.id ?? '')
              );
            }),
          }))
          .sort((a, b) => {
            const campaignCompare = a.campaignName.localeCompare(b.campaignName);
            if (campaignCompare !== 0) return campaignCompare;
            return a.taskName.localeCompare(b.taskName);
          }),
      })),
    }));
  }, [assignments]);

  return (
    <div className="overflow-x-auto">
      <Table className="w-full border-collapse border border-gray-300">
        <TableHeader>
          <TableRow className="bg-gray-50">
            <TableHead className="border border-gray-300 p-2 text-left">Operator</TableHead>
            <TableHead className="border border-gray-300 p-2 text-left">Store</TableHead>
            <TableHead className="border border-gray-300 p-2 text-left">Assignment ID</TableHead>
            <TableHead className="border border-gray-300 p-2 text-left">Campaign</TableHead>
            <TableHead className="border border-gray-300 p-2 text-left">
              {showRemovalBasis ? 'Task / Basis' : 'Task'}
            </TableHead>
            <TableHead className="border border-gray-300 p-2 text-left">Media Format</TableHead>
            <TableHead className="border border-gray-300 p-2 text-left">Expected</TableHead>
            <TableHead className="border border-gray-300 p-2 text-left">Delivered</TableHead>
            <TableHead className="border border-gray-300 p-2 text-left">Comments</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groupedAssignments.map((operatorGroup) => {
            const operatorTotalRows = operatorGroup.stores.reduce(
              (sum, store) => sum + store.campaignGroups.reduce((campaignSum, group) => campaignSum + group.assignments.length, 0),
              0
            );
            let operatorRowIndex = 0;

            return (
              <React.Fragment key={operatorGroup.operatorId}>
                {operatorGroup.stores.map((storeGroup) => (
                  <React.Fragment key={`${operatorGroup.operatorId}-${storeGroup.storeId}`}>
                    {storeGroup.campaignGroups.map((campaignGroup, campaignIndex) =>
                      campaignGroup.assignments.map((assignment, assignmentIndex) => (
                      <TableRow key={`${assignment.id || assignmentIndex}-${assignment.formatId || assignment.mediaFormatId || assignmentIndex}`} className={operatorRowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        {/* Operator Name - only show on first row of operator */}
                        {operatorRowIndex === 0 && (
                          <TableCell
                            rowSpan={operatorTotalRows}
                            className="border border-gray-300 p-2 font-bold align-top bg-blue-50"
                          >
                            {operatorGroup.operatorName}
                          </TableCell>
                        )}
                        {/* Store Name - only show on first row of store within operator */}
                        {campaignIndex === 0 && assignmentIndex === 0 && (
                          <TableCell
                            rowSpan={storeGroup.campaignGroups.reduce((sum, group) => sum + group.assignments.length, 0)}
                            className="border border-gray-300 p-2 font-medium align-top bg-gray-50"
                          >
                            {storeGroup.storeName}
                          </TableCell>
                        )}
                        {/* Assignment Identifier */}
                        <TableCell className="border border-gray-300 p-2 font-mono text-xs">
                          {(() => {
                            const realIdRaw = assignment.realAssignmentId ?? null;
                            const fallbackIdRaw = assignment.id ?? assignment.legacyAssignmentId ?? null;
                            const legacyCandidateRaw = assignment.legacyAssignmentId ?? assignment.id ?? null;

                            const realId = realIdRaw != null ? String(realIdRaw) : null;
                            const displayId = realId ?? (fallbackIdRaw != null ? String(fallbackIdRaw) : null);
                            const legacyId = legacyCandidateRaw != null ? String(legacyCandidateRaw) : null;
                            const normalizedLegacyId = legacyId?.replace(/^real-/, '') ?? null;
                            const showLegacy =
                              realId != null &&
                              normalizedLegacyId != null &&
                              realId !== normalizedLegacyId;

                            if (!displayId) {
                              return '—';
                            }

                            return (
                              <span>
                                {displayId}
                                {showLegacy ? (
                                  <span className="ml-1 text-muted-foreground">(legacy {legacyId})</span>
                                ) : null}
                              </span>
                            );
                          })()}
                        </TableCell>

                        {/* Campaign Name */}
                        {assignmentIndex === 0 && (
                          <TableCell
                            rowSpan={campaignGroup.assignments.length}
                            className="border border-gray-300 p-2 align-top"
                          >
                            {campaignGroup.campaignName}
                          </TableCell>
                        )}
                        {/* Task Name */}
                        {assignmentIndex === 0 && (
                          <TableCell
                            rowSpan={campaignGroup.assignments.length}
                            className="border border-gray-300 p-2 align-top"
                          >
                            <div className="flex flex-col gap-1">
                              <span>{campaignGroup.taskName}</span>
                              {showRemovalBasis ? (() => {
                                const label = getRemovalBasisGroupLabel(campaignGroup.assignments);
                                if (!label) return null;
                                return (
                                  <span className={`w-fit rounded-full border px-2 py-0.5 text-[11px] font-medium ${getRemovalBasisClassName(label)}`}>
                                    {label}
                                  </span>
                                );
                              })() : null}
                            </div>
                          </TableCell>
                        )}
                        {/* Media Format */}
                        <TableCell className="border border-gray-300 p-2">
                          {assignment.mediaFormatName || assignment.formatName || 'Unknown Format'}
                        </TableCell>
                        {/* Expected Quantity */}
                        <TableCell className="border border-gray-300 p-2">
                          {assignment.quantity || assignment.expectedQuantity || 0}
                        </TableCell>
                        {/* Delivered Quantity */}
                        <TableCell className="border border-gray-300 p-2">
                          {assignment.delivered || assignment.deliveredQuantity || '-'}
                        </TableCell>
                        {/* Comments */}
                        <TableCell className="border border-gray-300 p-2">
                          {assignment.comments || '-'}
                        </TableCell>
                        {(() => { operatorRowIndex++; return null; })()}
                      </TableRow>
                    )))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
